import React from 'react';
import { Box } from '@mui/material';
import '../pages/public/publicFonts.css';
import { useTranslation } from 'react-i18next';

// Maxulus logo with an optional product label ("CRM", "Google Ads Analiz") set in the logo's
// geometric face and navy; switches to the white-text logo on dark surfaces.
const NAVY = '#07163D';
const LOGO_RATIO = 615 / 120;

// Geometry measured on the 615x120 logo PNG: the wordmark's cap top sits at 19% and its
// baseline at 87.5% of the image height (the taller mark pushes the text below centre),
// so the label is aligned to those lines rather than to the image box.
const CAP_TOP = 0.19;
const CAP_HEIGHT = 0.683;
const BASELINE = 0.875;
// Outfit metrics (per 1px font size): cap height 0.717; with line-height 1 the baseline
// sits 0.87 from the top of the line box (ascent 1.0, descent 0.26).
const OUTFIT_CAP = 0.717;
const OUTFIT_BASELINE = 0.87;
// Outfit 700 matches the wordmark stroke width (measured at 2x: ~12px vs 13px at 750)
const LABEL_WEIGHT = 700;

interface MaxulusBrandProps {
  height?: number;
  label?: string;
  dark?: boolean;
}

const MaxulusBrand: React.FC<MaxulusBrandProps> = ({ height = 28, label, dark = false }) => {
  const { t } = useTranslation();
  const fontSize = (height * CAP_HEIGHT) / OUTFIT_CAP;
  const labelTop = height * BASELINE - fontSize * OUTFIT_BASELINE;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: `${Math.round(height * 0.42)}px`, minWidth: 0 }}>
      <Box component="img" src={dark ? '/assets/maxulus-logo-light.png' : '/assets/maxulus-logo.png'} alt={t('Maxulus')}
        width={Math.round(height * LOGO_RATIO)} height={height} sx={{ display: 'block', height, width: 'auto', flexShrink: 0 }} />
      {label && (
        <>
          <Box aria-hidden sx={{
            width: '1.5px', flexShrink: 0, borderRadius: 1,
            mt: `${height * (CAP_TOP - 0.04)}px`, height: `${height * (CAP_HEIGHT + 0.08)}px`,
            bgcolor: dark ? 'rgba(255,255,255,0.3)' : 'rgba(7,22,61,0.22)',
          }} />
          <Box component="span" sx={{
            mt: `${labelTop}px`, fontFamily: "'Outfit', sans-serif", fontWeight: LABEL_WEIGHT, fontSize: `${fontSize}px`,
            lineHeight: 1, letterSpacing: '-0.01em', color: dark ? '#FFFFFF' : NAVY, whiteSpace: 'nowrap',
          }}>
            {label}
          </Box>
        </>
      )}
    </Box>
  );
};

export default MaxulusBrand;
